import { useCreation, useMemoizedFn, useThrottleFn } from 'ahooks';
import classnames from 'classnames';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useReducer, useRef, useState } from 'react';
import { calc, pickDomAttrs } from '../_util/utils';
import EllipsisTooltip from '../ellipsis-tooltip';
import { useConfig } from '../hooks/useConfig';
import { useTheme } from '../hooks/useThemeSwitch';
import { NotFoundContent } from '../select';
import { useChartGroupContext } from './group';
import { ScrollLegend } from './scrollLegend';
import { bigValueFormatter, getColorConfig, getLightColor, getTooltipConfig, getyAxisMax, timeFormatter } from './share';

// 引入 echarts 核心模块，核心模块提供了 echarts 使用必须要的接口。
import * as echarts from 'echarts/core';
// 引入折线图、柱状图图表，图表后缀都为 Chart
import { BarChart, LineChart } from 'echarts/charts';
// 引入提示框，直角坐标系，数据集，内置数据转换器组件，组件后缀都为 Component
import { DataZoomComponent, DatasetComponent, GridComponent, LegendComponent, MarkAreaComponent, MarkLineComponent, TooltipComponent, TransformComponent } from 'echarts/components';
// 标签自动布局，全局过渡动画等特性
import { LabelLayout, UniversalTransition } from 'echarts/features';
// 引入 Canvas 渲染器，注意引入 CanvasRenderer 或者 SVGRenderer 是必须的一步
import { CanvasRenderer } from 'echarts/renderers';

// 注册必须的组件
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
echarts.use([BarChart, LineChart, LabelLayout, UniversalTransition, CanvasRenderer, DataZoomComponent, DatasetComponent, GridComponent, LegendComponent, MarkLineComponent, TooltipComponent, TransformComponent, MarkAreaComponent]);
const ChartMain = props => {
  var _xAxis$type, _yAxis$type;
  const {
    xAxis,
    yAxis,
    data = [],
    series,
    markLine,
    markArea,
    showDataZoom = true,
    showTooltip = true,
    selectedLegend,
    isUtcTimeZone = false,
    groupId,
    setGrid,
    cursor = 'default',
    baseClassName,
    prefixClass
  } = props;
  const xAxisType = (_xAxis$type = xAxis === null || xAxis === void 0 ? void 0 : xAxis.type) !== null && _xAxis$type !== void 0 ? _xAxis$type : 'time';
  const yAxisType = (_yAxis$type = yAxis === null || yAxis === void 0 ? void 0 : yAxis.type) !== null && _yAxis$type !== void 0 ? _yAxis$type : 'bigValue';
  const [_, colors] = useTheme();
  const {
    locale: {
      Chart: ChartLocale
    }
  } = useConfig();
  const chartGroup = useChartGroupContext();
  const containerRef = useRef(null);
  const chart = useRef();
  const resizeObserver = useRef();
  const [chartReady, setChartReady] = useState(false);
  const throttleResize = useThrottleFn(() => {
    var _chart$current;
    (_chart$current = chart.current) === null || _chart$current === void 0 || _chart$current.resize();
  }, {
    wait: 100
  });
  const setChartOption = opt => {
    var _chart$current2;
    (_chart$current2 = chart.current) === null || _chart$current2 === void 0 || _chart$current2.setOption(opt, {
      lazyUpdate: true // 开启延迟更新，否则有很大概率报错
    });
  };
  const yFormatter = useMemoizedFn((value, place) => {
    var _yAxis$unit;
    if (yAxis !== null && yAxis !== void 0 && yAxis.formatter) {
      return yAxis.formatter(value, place) + '';
    }
    const unit = (_yAxis$unit = yAxis === null || yAxis === void 0 ? void 0 : yAxis.unit) !== null && _yAxis$unit !== void 0 ? _yAxis$unit : '';
    if (yAxisType === 'bigValue') {
      return bigValueFormatter(value) + unit;
    }
    return (value !== null && value !== void 0 ? value : '-') + ' ' + unit;
  });
  const xFormatter = useMemoizedFn(value => {
    if (xAxis !== null && xAxis !== void 0 && xAxis.formatter) {
      return xAxis.formatter(value);
    }
    if (xAxisType === 'time') {
      return timeFormatter(value, 'YMDHms', isUtcTimeZone);
    }
    return value;
  });
  const dataSource = useCreation(() => {
    const dataSource = data.length ? Array.isArray(data[0]) ? data : [data] : [[]];
    return dataSource.map(source => source !== null && source !== void 0 && source.length ? source : []);
  }, [data]);
  const {
    hasBar,
    maxDataSourceLength
  } = useCreation(() => {
    const hasBar = series.some(item => {
      return item.type === 'bar';
    });
    const maxDataSourceLength = Math.max(...dataSource.map(source => source.length));
    return {
      hasBar,
      maxDataSourceLength
    };
  }, [series, dataSource]);
  const {
    _markLine,
    _markArea,
    maxMarkValue
  } = useCreation(() => {
    var _markLine$flatMap, _markArea$flatMap;
    const _markLine = markLine ? Array.isArray(markLine) ? markLine : [markLine] : [];
    const _markArea = markArea ? Array.isArray(markArea) ? markArea : [markArea] : [];
    const markLineValues = (_markLine$flatMap = _markLine.flatMap(it => typeof it.value === 'number' ? [it.value] : [])) !== null && _markLine$flatMap !== void 0 ? _markLine$flatMap : [];
    const markAreaValues = (_markArea$flatMap = _markArea.flatMap(it => {
      var _it$value;
      return typeof ((_it$value = it.value) === null || _it$value === void 0 ? void 0 : _it$value[1]) === 'number' ? [it.value[1]] : [];
    })) !== null && _markArea$flatMap !== void 0 ? _markArea$flatMap : [];
    return {
      _markLine,
      _markArea,
      maxMarkValue: markLineValues.length || markAreaValues.length ? Math.max(...markLineValues, ...markAreaValues) : undefined
    };
  }, [markLine, markArea]);
  useEffect(() => {
    if (chartReady && chart.current) {
      var _ref;
      chart.current.group = (_ref = groupId !== null && groupId !== void 0 ? groupId : chartGroup === null || chartGroup === void 0 ? void 0 : chartGroup.id) !== null && _ref !== void 0 ? _ref : 'NO_GROUP';
    }
  }, [chartReady, groupId, chartGroup === null || chartGroup === void 0 ? void 0 : chartGroup.id]);
  useEffect(() => {
    setTimeout(() => {
      if (!containerRef.current) {
        return;
      }

      // 创建 chart 实例
      if (!chart.current) {
        chart.current = echarts.init(containerRef.current, null, {
          locale: 'EN'
        });
      }

      // 监听大小变化
      resizeObserver.current = new ResizeObserver(entries => {
        entries.forEach(throttleResize.run);
      });
      resizeObserver.current.observe(containerRef.current);

      // 初始化完成
      setChartReady(true);
    }, 10);
    return () => {
      var _chart$current3, _resizeObserver$curre;
      (_chart$current3 = chart.current) === null || _chart$current3 === void 0 || _chart$current3.dispose();
      (_resizeObserver$curre = resizeObserver.current) === null || _resizeObserver$curre === void 0 || _resizeObserver$curre.disconnect();
    };
  }, []);
  useLayoutEffect(() => {
    if (!chartReady) {
      return;
    }
    setChartOption({
      dataset: dataSource.map(source => ({
        source
      }))
    });
  }, [chartReady, dataSource]);

  // 配置 color, grid, xAxis, yAxis
  useLayoutEffect(() => {
    var _grid$top, _grid$right, _grid$bottom, _grid$left, _xAxis$minInterval, _xAxis$formatter, _yAxis$minInterval;
    if (!chartReady) {
      return;
    }
    const axisConfig = {
      nameTextStyle: {
        color: colors["--".concat(prefixClass, "-onSurface-med-color")]
      },
      nameGap: 16,
      axisLine: {
        show: true,
        lineStyle: {
          color: colors["--".concat(prefixClass, "-primary-disabled-states")]
        }
      },
      axisTick: {
        show: true,
        lineStyle: {
          color: colors["--".concat(prefixClass, "-primary-disabled-states")]
        }
      },
      axisLabel: {
        show: true,
        color: colors["--".concat(prefixClass, "-onSurface-med-color")]
      }
    };
    const grid = setGrid === null || setGrid === void 0 ? void 0 : setGrid({
      showDataZoom
    });
    setChartOption({
      color: getColorConfig({
        prefixClass,
        colors
      }),
      grid: {
        top: (_grid$top = grid === null || grid === void 0 ? void 0 : grid.top) !== null && _grid$top !== void 0 ? _grid$top : 16,
        right: (_grid$right = grid === null || grid === void 0 ? void 0 : grid.right) !== null && _grid$right !== void 0 ? _grid$right : 40,
        bottom: (_grid$bottom = grid === null || grid === void 0 ? void 0 : grid.bottom) !== null && _grid$bottom !== void 0 ? _grid$bottom : showDataZoom ? 80 : 40,
        left: (_grid$left = grid === null || grid === void 0 ? void 0 : grid.left) !== null && _grid$left !== void 0 ? _grid$left : 64
      },
      xAxis: {
        ...axisConfig,
        type: xAxisType,
        boundaryGap: hasBar,
        splitLine: {
          show: false
        },
        minInterval: (_xAxis$minInterval = xAxis === null || xAxis === void 0 ? void 0 : xAxis.minInterval) !== null && _xAxis$minInterval !== void 0 ? _xAxis$minInterval : xAxisType === 'time' ? 1000 * 60 * 5 + 1 : 1,
        maxInterval: xAxis === null || xAxis === void 0 ? void 0 : xAxis.maxInterval,
        axisLabel: {
          show: true,
          color: colors["--".concat(prefixClass, "-onSurface-med-color")],
          hideOverlap: true,
          showMinLabel: maxDataSourceLength >= 2 && maxDataSourceLength <= 5 ? true : undefined,
          showMaxLabel: maxDataSourceLength >= 2 && maxDataSourceLength <= 5 ? true : undefined,
          formatter: (_xAxis$formatter = xAxis === null || xAxis === void 0 ? void 0 : xAxis.formatter) !== null && _xAxis$formatter !== void 0 ? _xAxis$formatter : xAxisType === 'time' ? ChartLocale.xAxisTimeFormat : undefined
        }
      },
      yAxis: {
        ...axisConfig,
        type: 'value',
        minInterval: (_yAxis$minInterval = yAxis === null || yAxis === void 0 ? void 0 : yAxis.minInterval) !== null && _yAxis$minInterval !== void 0 ? _yAxis$minInterval : yAxisType === 'value' ? 0.1 : 1,
        maxInterval: yAxis === null || yAxis === void 0 ? void 0 : yAxis.maxInterval,
        min: p => {
          var _yAxis$min, _yAxis$min2;
          const customMin = (_yAxis$min = yAxis === null || yAxis === void 0 || (_yAxis$min2 = yAxis.min) === null || _yAxis$min2 === void 0 ? void 0 : _yAxis$min2.call(yAxis, {
            ...p,
            maxMark: maxMarkValue
          })) !== null && _yAxis$min !== void 0 ? _yAxis$min : null;
          if (typeof customMin === 'number') {
            return customMin;
          }
          return 0;
        },
        max: p => {
          var _p$max, _yAxis$max, _yAxis$max2;
          const dataMax = (_p$max = p.max) !== null && _p$max !== void 0 ? _p$max : 0;
          const customMax = (_yAxis$max = yAxis === null || yAxis === void 0 || (_yAxis$max2 = yAxis.max) === null || _yAxis$max2 === void 0 ? void 0 : _yAxis$max2.call(yAxis, {
            ...p,
            maxMark: maxMarkValue
          })) !== null && _yAxis$max !== void 0 ? _yAxis$max : null;
          if (typeof customMax === 'number') {
            return customMax;
          }
          return getyAxisMax(Math.max(maxMarkValue !== null && maxMarkValue !== void 0 ? maxMarkValue : 0, dataMax), yAxisType === 'percentage' ? 100 : 0);
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: colors["--".concat(prefixClass, "-primary-disabled-states")]
          }
        },
        axisLine: {
          show: false
        },
        axisLabel: {
          show: true,
          color: colors["--".concat(prefixClass, "-onSurface-med-color")],
          showMinLabel: false,
          // 隐藏最小刻度
          formatter: value => yFormatter(value, 'yAxis')
        }
      }
    });
  }, [chartReady, colors, xAxis, yAxis, showDataZoom, hasBar, maxMarkValue, maxDataSourceLength]);

  // 设置 series
  useLayoutEffect(() => {
    if (!chartReady || Object.keys(colors).length === 0) {
      return;
    }
    const chartSeries = series.map((item, i) => {
      var _item$lineStyle$smoot, _item$lineStyle, _item$dataKey;
      const color = item.color ? colors["--".concat(prefixClass, "-").concat(item.color, "-senior-color")] : undefined;
      if (item.type === 'bar') {
        return {
          name: item.name,
          type: 'bar',
          barGap: 0,
          itemStyle: {
            color
          },
          encode: {
            x: xAxis.key,
            y: item.key
          }
        };
      }
      return {
        name: item.name,
        type: 'line',
        itemStyle: {
          color
        },
        lineStyle: {
          width: 1,
          type: 'solid'
        },
        symbolSize: 2,
        // 标记点大小
        showSymbol: false,
        // false时只有在hover下显示标记点
        smooth: (_item$lineStyle$smoot = (_item$lineStyle = item.lineStyle) === null || _item$lineStyle === void 0 ? void 0 : _item$lineStyle.smooth) !== null && _item$lineStyle$smoot !== void 0 ? _item$lineStyle$smoot : false,
        // 开启圆滑曲线会相对较卡，建议关闭
        datasetIndex: dataSource.length > 1 ? i : 0,
        encode: {
          x: xAxis.key,
          y: (_item$dataKey = item.dataKey) !== null && _item$dataKey !== void 0 ? _item$dataKey : item.key
        }
      };
    });
    // mark line
    _markLine.forEach(item => {
      const color = item.color ? colors["--".concat(prefixClass, "-").concat(item.color, "-senior-color")] : colors["--".concat(prefixClass, "-onSurface-fontHigh-color")];
      chartSeries.push({
        data: [],
        name: item.key,
        type: 'line',
        symbol: 'none',
        markLine: {
          label: {
            show: !!item.name,
            position: 'insideEndTop',
            color,
            formatter: item.name,
            fontSize: 12,
            textBorderWidth: 0
          },
          lineStyle: {
            color,
            width: 1,
            type: item.stroke
          },
          silent: true,
          // 不响应和触发鼠标事件
          symbol: 'none',
          data: typeof item.value === 'number' ? [{
            yAxis: item.value
          }] : null
        }
      });
    });
    // mark area
    _markArea.forEach(item => {
      const color = item.color ? colors["--".concat(prefixClass, "-").concat(item.color, "-senior-color")] : colors["--".concat(prefixClass, "-onSurface-fontHigh-color")];
      chartSeries.push({
        data: [],
        name: item.key,
        type: 'line',
        symbol: 'none',
        markArea: {
          label: {
            show: !!item.name,
            position: 'insideTopRight',
            offset: [0, -24],
            color,
            formatter: item.name,
            fontSize: 12,
            align: 'right',
            textBorderWidth: 0
          },
          itemStyle: {
            color: getLightColor(color, 0.15)
          },
          silent: true,
          // 不响应和触发鼠标事件
          data: item.value ? [[{
            yAxis: item.value[0]
          }, {
            yAxis: item.value[1]
          }]] : null
        },
        markLine: {
          label: {
            show: false
          },
          lineStyle: {
            color,
            width: 1,
            type: item.stroke
          },
          silent: true,
          // 不响应和触发鼠标事件
          symbol: 'none',
          data: item.value ? [{
            yAxis: item.value[0]
          }, {
            yAxis: item.value[1]
          }] : null
        }
      });
    });
    setChartOption({
      useUTC: isUtcTimeZone,
      series: chartSeries,
      legend: {
        show: false,
        selected: series.reduce((acc, cur) => {
          var _selectedLegend$cur$k;
          acc[cur.name] = (_selectedLegend$cur$k = selectedLegend[cur.key]) !== null && _selectedLegend$cur$k !== void 0 ? _selectedLegend$cur$k : true;
          return acc;
        }, {})
      }
    });
  }, [chartReady, colors, xAxis.key, series, _markLine, _markArea, selectedLegend, isUtcTimeZone, dataSource.length]);

  // 设置 dataZoom
  useLayoutEffect(() => {
    if (!chartReady || Object.keys(colors).length === 0) {
      return;
    }
    setChartOption({
      dataZoom: [{
        type: 'slider',
        start: 0,
        end: 100,
        show: showDataZoom,
        backgroundColor: colors["--".concat(prefixClass, " -primary-disabled-states")],
        fillerColor: colors["--".concat(prefixClass, "-primary-hover-states")],
        borderColor: 'transparent',
        dataBackground: {
          lineStyle: {
            color: colors["--".concat(prefixClass, "-outline-enabled-states")]
          },
          areaStyle: {
            color: colors["--".concat(prefixClass, "-primary-disabled-states")],
            opacity: 1
          }
        },
        selectedDataBackground: {
          lineStyle: {
            color: colors["--".concat(prefixClass, "-outline-enabled-states")]
          },
          areaStyle: {
            color: colors["--".concat(prefixClass, "-primary-disabled-states")],
            opacity: 1
          }
        },
        textStyle: {
          color: colors["--".concat(prefixClass, "-onSurface-med-color")]
        },
        brushStyle: {
          color: colors["--".concat(prefixClass, "-primary-hover-states")]
        },
        handleStyle: {
          color: colors["--".concat(prefixClass, "-elevation-5")],
          borderColor: colors["--".concat(prefixClass, "-onSurface-low-color")]
        },
        moveHandleStyle: {
          color: colors["--".concat(prefixClass, "-onSurface-low-color")]
        },
        emphasis: {
          handleStyle: {
            borderColor: colors["--".concat(prefixClass, "-primary-med-color")]
          },
          moveHandleStyle: {
            color: colors["--".concat(prefixClass, "-primary-med-color")]
          }
        },
        labelFormatter: ''
      }]
    });
  }, [chartReady, colors, dataSource, showDataZoom]);

  // 配置 tooltip
  useLayoutEffect(() => {
    if (!chartReady || Object.keys(colors).length === 0) {
      return;
    }
    setChartOption({
      tooltip: showTooltip ? {
        ...getTooltipConfig({
          prefixClass,
          colors,
          extra: showTooltip === true ? undefined : showTooltip.extra,
          xFormatter,
          yFormatter: value => yFormatter(value, 'tooltip')
        }),
        axisPointer: {
          lineStyle: {
            type: 'solid',
            width: 1,
            color: colors["--".concat(prefixClass, "-outline-enabled-states")]
          }
        }
      } : undefined
    });
  }, [chartReady, colors, showTooltip, series, yAxis === null || yAxis === void 0 ? void 0 : yAxis.unit]);
  return /*#__PURE__*/_jsx("div", {
    ref: containerRef,
    className: classnames("".concat(baseClassName, "--container"), {
      ['cursor-pointer']: cursor === 'pointer',
      ['cursor-crosshair']: cursor === 'crosshair'
    })
  });
};
const Chart = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    data,
    legend,
    legendScroll,
    width,
    height,
    markLine,
    markArea,
    prefixClass = 'zen-design',
    //TODO 写provider时统一改掉
    className,
    style,
    ...restProps
  } = props;
  const baseClassName = "".concat(prefixClass, "-mix-line-bar-chart");
  const [_, colors] = useTheme();
  const series = useCreation(() => {
    var _props$series;
    return (_props$series = props.series) !== null && _props$series !== void 0 ? _props$series : [];
  }, [JSON.stringify(props.series)]);
  const showNoData = useCreation(() => {
    return !(data !== null && data !== void 0 && data.length) || data.every(item => Array.isArray(item) && !item.length);
  }, [data]);
  const [selectedLegend, setSelectedLegend] = useReducer((a, b) => {
    if (typeof b === 'string') {
      return {
        ...a,
        [b]: !a[b]
      };
    }
    return {
      ...a,
      ...b
    };
  }, {});
  useLayoutEffect(() => {
    setSelectedLegend(series.reduce((acc, cur) => {
      var _selectedLegend$cur$k2;
      acc[cur.key] = (_selectedLegend$cur$k2 = selectedLegend[cur.key]) !== null && _selectedLegend$cur$k2 !== void 0 ? _selectedLegend$cur$k2 : true;
      return acc;
    }, {}));
  }, [series]);
  useImperativeHandle(ref, () => ({
    toggleSeriesVisible: setSelectedLegend
  }));
  const legendEle = useCreation(() => {
    if (!Object.keys(colors).length || !legend || !series.length || legend === true && series.length < 2) {
      return null;
    }
    const legendItems = legend === true ? series.map(item => ({
      key: item.key
    })) : legend;
    const legendItemColumnNum = legendItems.reduce((res, cur) => {
      var _cur$columns$length, _cur$columns;
      return Math.max(res, (_cur$columns$length = (_cur$columns = cur.columns) === null || _cur$columns === void 0 ? void 0 : _cur$columns.length) !== null && _cur$columns$length !== void 0 ? _cur$columns$length : 0);
    }, 0);
    const legendElements = legendItems.map(legendItem => {
      const markLineItem = (Array.isArray(markLine) ? markLine : markLine ? [markLine] : []).find(m => typeof m.value === 'number' && m.key === legendItem.key);
      if (markLineItem) {
        var _legendItem$name, _markLineItem$stroke, _legendItem$columns;
        const markLineColor = colors["--".concat(prefixClass, "-").concat(markLineItem.color, "-senior-color")] || colors["--".concat(prefixClass, "-onSurface-fontHigh-color")];
        return {
          key: markLineItem.key,
          name: (_legendItem$name = legendItem.name) !== null && _legendItem$name !== void 0 ? _legendItem$name : markLineItem.name,
          type: 'markLine',
          stroke: (_markLineItem$stroke = markLineItem.stroke) !== null && _markLineItem$stroke !== void 0 ? _markLineItem$stroke : 'dashed',
          color: markLineColor,
          columns: (_legendItem$columns = legendItem.columns) !== null && _legendItem$columns !== void 0 ? _legendItem$columns : []
        };
      }
      const idx = series.findIndex(it => it.key === legendItem.key);
      if (idx > -1) {
        var _legendItem$name2, _legendItem$columns2;
        const item = series[idx];
        return {
          key: item.key,
          name: (_legendItem$name2 = legendItem.name) !== null && _legendItem$name2 !== void 0 ? _legendItem$name2 : item.name,
          type: 'normal',
          stroke: 'solid',
          color: item.color ? colors["--".concat(prefixClass, "-").concat(item.color, "-senior-color")] : getColorConfig({
            prefixClass,
            colors
          })[idx],
          columns: (_legendItem$columns2 = legendItem.columns) !== null && _legendItem$columns2 !== void 0 ? _legendItem$columns2 : []
        };
      }
      return null;
    });
    if (legendItemColumnNum === 0) {
      return /*#__PURE__*/_jsx(ScrollLegend, {
        selectedLegend: selectedLegend,
        legend: legendElements,
        prefixClass: baseClassName,
        onChange: setSelectedLegend
      });
    }
    const innerStyle = legendScroll && legendScroll < legendElements.filter(it => !!it).length ? {
      overflowY: 'auto',
      maxHeight: calc.add(calc.mul(legendScroll, 42), calc.mul(calc.sub(legendScroll, 1), 17))
    } : {};
    return /*#__PURE__*/_jsx("div", {
      className: "".concat(baseClassName, "--legend"),
      children: /*#__PURE__*/_jsx("div", {
        className: "".concat(baseClassName, "--legend-inner"),
        style: innerStyle,
        children: legendElements.map((item, idx) => {
          if (!item) {
            return null;
          }
          const isMarkLine = item.type === 'markLine';
          return /*#__PURE__*/_jsxs("div", {
            className: classnames('legend-row', {
              ['top-split-line']: legendItemColumnNum > 0
            }),
            children: [/*#__PURE__*/_jsx("div", {
              className: "legend-column",
              children: /*#__PURE__*/_jsxs("div", {
                className: "legend-column-inner",
                style: {
                  cursor: isMarkLine ? 'default' : 'pointer'
                },
                onClick: () => {
                  !isMarkLine && setSelectedLegend(item.key);
                },
                children: [/*#__PURE__*/_jsx("div", {
                  className: "legend-column--label",
                  children: typeof item.name === 'string' ? /*#__PURE__*/_jsx("div", {
                    className: "ellipsis-text",
                    style: {
                      height: 18
                    },
                    children: /*#__PURE__*/_jsx(EllipsisTooltip, {
                      children: item.name
                    })
                  }) : item.name
                }), /*#__PURE__*/_jsx("div", {
                  className: "legend-column--value",
                  children: /*#__PURE__*/_jsx("div", {
                    className: "legend-color",
                    style: {
                      opacity: selectedLegend[item.key] || isMarkLine ? '1' : '0.5'
                    },
                    children: /*#__PURE__*/_jsxs("span", {
                      style: {
                        color: item.color,
                        columnGap: item.stroke === 'dashed' ? 2 : 0
                      },
                      children: [/*#__PURE__*/_jsx("i", {}), /*#__PURE__*/_jsx("i", {}), /*#__PURE__*/_jsx("i", {}), item.stroke === 'solid' && /*#__PURE__*/_jsx("i", {})]
                    })
                  })
                })]
              })
            }), legendItemColumnNum > 0 && Array.from(Array(legendItemColumnNum)).map((_, i) => {
              var _legendColumn$value;
              const legendColumn = item.columns[i];
              return /*#__PURE__*/_jsx("div", {
                className: "legend-column",
                children: !!(legendColumn !== null && legendColumn !== void 0 && legendColumn.label) && /*#__PURE__*/_jsxs("div", {
                  className: "legend-column-inner",
                  children: [/*#__PURE__*/_jsx("div", {
                    className: "legend-column--label",
                    children: typeof legendColumn.label === 'string' ? /*#__PURE__*/_jsx("div", {
                      className: "ellipsis-text",
                      style: {
                        height: 18
                      },
                      children: /*#__PURE__*/_jsx(EllipsisTooltip, {
                        children: legendColumn.label
                      })
                    }) : legendColumn.label
                  }), /*#__PURE__*/_jsx("div", {
                    className: "legend-column--value",
                    children: typeof legendColumn.value === 'string' && !!legendColumn.value ? /*#__PURE__*/_jsx("div", {
                      className: "ellipsis-text",
                      style: {
                        height: 20
                      },
                      children: /*#__PURE__*/_jsx(EllipsisTooltip, {
                        children: legendColumn.value
                      })
                    }) : (_legendColumn$value = legendColumn.value) !== null && _legendColumn$value !== void 0 ? _legendColumn$value : '-'
                  })]
                })
              }, i);
            })]
          }, item.key);
        })
      })
    });
  }, [legend, selectedLegend, legendScroll, series, markLine, colors]);
  const attrs = pickDomAttrs(restProps);
  return /*#__PURE__*/_jsx("div", {
    ...attrs.data,
    className: classnames(baseClassName, className),
    style: style,
    children: /*#__PURE__*/_jsx("div", {
      className: "".concat(baseClassName, "--main"),
      style: {
        width,
        height
      },
      children: showNoData ? /*#__PURE__*/_jsx("div", {
        className: "".concat(baseClassName, "--empty"),
        children: /*#__PURE__*/_jsx(NotFoundContent, {})
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ChartMain, {
          ...props,
          data: data,
          series: series,
          selectedLegend: selectedLegend,
          prefixClass: prefixClass,
          baseClassName: baseClassName
        }), legendEle]
      })
    })
  });
});
export default Chart;